/**
 * Applies the listener's private volume opinions to the live room.
 *
 * Re-applied on every roster change because LiveKit forgets a participant's
 * gain when their track is republished (a device switch, a reconnect), and a
 * person you turned down coming back at full volume is exactly the moment the
 * feature would lose the user's trust.
 */
import type { MediaClient } from '@/features/meeting/media/contract';
import { gainFor, voicePrefs } from '../store/voicePrefs';
import { voiceSessionStore } from '../store/voiceSessionStore';

/** Last applied gain per SFU identity, so a no-op sync costs nothing. */
const applied = new Map<string, number>();

export function applyLocalGains(client: MediaClient): void {
  const prefs = voicePrefs.get();
  const deafened = voiceSessionStore.get().deafened;
  const snapshot = client.getSnapshot();

  const live = new Set<string>();
  for (const participant of snapshot.participants) {
    if (participant.isLocal) continue;
    live.add(participant.id);
    const gain = deafened ? 0 : gainFor(prefs, participant.userId);
    if (applied.get(participant.id) === gain) continue;
    applied.set(participant.id, gain);
    client.setParticipantVolume(participant.id, gain);
  }

  for (const id of [...applied.keys()]) {
    if (!live.has(id)) applied.delete(id);
  }
}

/** Called on leave so a new session does not inherit the previous room's map. */
export function resetLocalGains(): void {
  applied.clear();
}
